import { computed, makeAutoObservable, observable } from 'mobx'
import PendingQueries from 'singletons/PendingQueries'
import DailyIframe from '@daily-co/daily-js'
import GlobalEvents from 'singletons/GlobalEvents'
import { CallInstance, CallInstanceType } from 'types/CallInstance'
import DeviceSettings from 'models/DeviceSettings'
import User from 'singletons/User'

const isDeviceAvailable = async (constraints) => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints)

    return !!stream
  } catch (error) {
    return false
  }
}

const getInitialMediaDevices = async () => {
  try {
    const enumeratedDevices = await navigator.mediaDevices.enumerateDevices()

    const storedCameraId = localStorage.getItem('cameraDeviceId')
    const storedMicrophoneId = localStorage.getItem('microphoneDeviceId')
    const storedSpeakerId = localStorage.getItem('speakerDeviceId')

    const videoSource =
      storedCameraId &&
      enumeratedDevices.some((i) => i.kind === 'videoinput' && i.deviceId === storedCameraId) &&
      (await isDeviceAvailable({ video: { deviceId: storedCameraId } }))
        ? storedCameraId
        : undefined
    const audioSource =
      storedMicrophoneId &&
      enumeratedDevices.some((i) => i.kind === 'audioinput' && i.deviceId === storedMicrophoneId) &&
      (await isDeviceAvailable({ audio: { deviceId: storedMicrophoneId } }))
        ? storedMicrophoneId
        : undefined

    const audioOutputSource =
      storedSpeakerId &&
      enumeratedDevices.some((i) => i.kind === 'audiooutput' && i.deviceId === storedSpeakerId) &&
      (await isDeviceAvailable({ audio: { deviceId: storedSpeakerId } }))
        ? storedSpeakerId
        : undefined

    localStorage.setItem('cameraDeviceId', videoSource || '')
    localStorage.setItem('microphoneDeviceId', audioSource || '')
    localStorage.setItem('speakerDeviceId', audioOutputSource || '')

    return { audioSource, videoSource, audioOutputSource }
  } catch (error) {
    console.log(error)
    return {}
  }
}

const videoProcessorDefault = Object.freeze({
  type: 'none',
})

const videoProcessorWithBackground = Object.freeze({
  type: 'background-image',
  config: {
    source: process.env.REACT_APP_DAILY_VIDEO_BACKGROUND_IMAGE,
  },
})

class VideoChat implements CallInstance {
  room = null
  type: CallInstanceType = 'video'
  connectId = ''
  roomName = ''
  callObject = null
  deviceSettings = new DeviceSettings(this)

  constructor() {
    makeAutoObservable(this, {
      room: observable.ref,
      callObject: observable.ref,
      active: computed,
    })
  }

  *disconnect() {
    yield this.callObject?.destroy()
    this.deviceSettings.reset()
    this.callObject = null
    this.room = null
  }

  get active() {
    return !!this.room
  }

  destructor = () => {
    this.disconnect()
  }

  handleCameraError = (e) => {
    this.destructor()

    const systemMessage = e?.error?.msg
    GlobalEvents.emit('visitStartVideo', {
      status: 'error',
      description: `
      Your camera is turned off and needs permission to open. 
      ${systemMessage ? `System message: ${systemMessage}` : ''}`,
    })
  };

  *connect(connectId, roomName) {
    this.connectId = connectId
    this.roomName = roomName
    yield this.connectAsync()
  }

  async connectAsync() {
    await this.disconnect()
    const { audioOutputSource, ...inputMediaDevices } = await getInitialMediaDevices()
    const token = this.connectId
    if (token && this.roomName) {
      const queryId = PendingQueries.add('#startVideo', this.roomName)
      try {
        const room = { url: `${process.env.REACT_APP_DAILY_DOMAIN}/${this.roomName}`, token }

        this.room = room

        const options = {
          ...this.room,
          ...inputMediaDevices,
        }

        this.callObject = DailyIframe.createCallObject(options)
        this.callObject.on('camera-error', this.handleCameraError)
        await this.callObject.startCamera()

        if (audioOutputSource) {
          await this.callObject.setOutputDeviceAsync({ outputDeviceId: audioOutputSource })
        }

        // Enable noise cancellation by default for all calls
        await this.publishAudioNoiseCancellation(true)

        if (User.isVirtualBackgroundEnabled) {
          await this.publishVideoBackground(this.deviceSettings.storedVideoBackground)
        }
        await this.callObject.join()
      } finally {
        PendingQueries.remove('#startVideo', queryId)
      }
    }
  }

  async publishNewCamera(videoDeviceId) {
    if (videoDeviceId) {
      this.callObject?.setInputDevicesAsync({ videoDeviceId })
    }
  }

  async publishNewMicrophone(microphoneDeviceId) {
    if (microphoneDeviceId) {
      this.callObject?.setInputDevicesAsync({ audioDeviceId: microphoneDeviceId })
    }
  }

  async publishNewSpeaker(speakerDeviceId) {
    if (speakerDeviceId) {
      this.callObject?.setOutputDeviceAsync({ outputDeviceId: speakerDeviceId })
    }
  }

  handleVideoBackgroundError = (isCameraOnInitially) => (e) => {
    if (['video-processor-error', 'input-settings-error'].includes(e?.type)) {
      this.callObject?.setLocalVideo(!!isCameraOnInitially)
      this.callObject?.off('nonfatal-error', this.handleVideoBackgroundError(isCameraOnInitially))
    }
  }

  async publishAudioNoiseCancellation(enableNoiseCancellation = true) {
    this.callObject?.updateInputSettings({
      audio: { processor: { type: enableNoiseCancellation ? 'noise-cancellation' : 'none' } },
    })
  }

  async publishVideoBackground(hasBackground) {
    if (typeof hasBackground === 'boolean') {
      const isCameraOnNow = !!this.callObject?.participants()?.local?.video
      this.callObject?.on('nonfatal-error', this.handleVideoBackgroundError(isCameraOnNow))

      this.callObject?.updateInputSettings({
        video: { processor: hasBackground ? videoProcessorWithBackground : videoProcessorDefault },
      })

      if (hasBackground) {
        this.callObject?.setLocalVideo(true)
      }
    }
  }

  async applyDeviceSettingsChanges({
    videoDeviceId,
    microphoneDeviceId,
    speakerDeviceId,
    hasBackground,
  }: {
    videoDeviceId?: string
    microphoneDeviceId?: string
    speakerDeviceId?: string
    hasBackground?: boolean
  }) {
    await this.publishNewCamera(videoDeviceId)
    await this.publishNewMicrophone(microphoneDeviceId)
    await this.publishNewSpeaker(speakerDeviceId)
    await this.publishAudioNoiseCancellation(true)
    if (User.isVirtualBackgroundEnabled) {
      await this.publishVideoBackground(hasBackground)
    }
  }
}

export default VideoChat
